import React, { useState } from 'react';
import { forTemplate } from 'app/common/lib/util/i18n';
import { uniq, union, flatten } from 'underscore';
import { Noun, Verb, trackUe } from '@trello/analytics';
import Organization from 'app/scripts/models/organization';

const ApiPromise = require('app/scripts/network/api-promise');
const MemberMultiSelectAutocompleteComponent = require('app/scripts/views/member/member-multi-select-autocomplete-component');

const format = forTemplate('org_members');
import styles from './invite-team-members-autocomplete-component.less';

interface InviteTeamMembersAutocompleteComponentProps {
  children: React.ReactNode;
  orgId: string;
  onEmailSelected?: () => void;
  optionsAddMembers: {
    invitationMessage: string;
    changedInviteMessage: boolean;
  };
  model: Organization;
  placeholder: string;
  fullWidthButton?: boolean;
  onInviteTeamMembersSuccess?: () => void;
}

export const InviteTeamMembersAutocompleteComponent: React.FunctionComponent<InviteTeamMembersAutocompleteComponentProps> = ({
  children,
  placeholder,
  orgId,
  model,
  onEmailSelected = () => {},
  optionsAddMembers,
  fullWidthButton,
  onInviteTeamMembersSuccess,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [selectedMemberOptions, setSelectedMemberOptions] = useState<any[]>([]);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const invitationMessage = optionsAddMembers
    ? optionsAddMembers.invitationMessage
    : '';
  const changedInviteMessage = optionsAddMembers
    ? optionsAddMembers.changedInviteMessage
    : false;

  const trackSubmit = () => {
    const prepositionalObj = !invitationMessage
      ? 'with no invite message'
      : changedInviteMessage
      ? 'with personalized invite message'
      : 'with canned invite message';

    trackUe({
      category: Noun.TEAM_VIEW,
      verb: Verb.SENDS,
      directObj: Noun.TEAM_INVITATION,
      prepositionalObj,
      indirectObj: `with ${selectedMemberOptions.length} invites`,
      method: 'by clicking invite to team button on team members page',
      context: {
        teamId: model.get('id'),
      },
    });
  };

  const onSubmit = () => {
    trackSubmit();

    const newMembers = model.addMembers(selectedMemberOptions, {
      invitationMessage,
    });
    if (onInviteTeamMembersSuccess) {
      setSubmitDisabled(true);
      onInviteTeamMembersSuccess();
    }
    return newMembers;
  };

  const onAutocompleteSearch = (query: string) => {
    if (query.length > 2) {
      return ApiPromise({
        url: '/1/search/members/',
        type: 'get',
        data: {
          query: query,
          idOrganization: orgId,
        },
        dataType: 'json',
      });
    } else {
      return [];
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSelectMemberOption = (member: any) => {
    setSelectedMemberOptions((selectedMembers) =>
      uniq(union(selectedMembers, flatten([member])), false, (item) =>
        item.username ? item.username : item.email,
      ),
    );

    onEmailSelected();
  };

  const onRemoveMemberOption = (idx: number) => {
    setSelectedMemberOptions((selectedMembers) =>
      selectedMembers.filter((_, i) => i !== idx),
    );
  };

  const autocompleteProps = {
    placeholder: placeholder,
    onSubmit: onSubmit,
    onAutocompleteSearch: onAutocompleteSearch,
    displayDetails: true,
    displayUsername: true,
    submitText: format('invite-to-team'),
    onRemoveMemberOption: onRemoveMemberOption,
    onSelectMemberOption: onSelectMemberOption,
    shouldFocusOnSelectMemberOption: true,
    selectedOptions: selectedMemberOptions,
    children: children,
    fullWidthButton: fullWidthButton,
    hideInviteLink: true,
    submitDisabled: submitDisabled || !model,
  };

  return (
    <div className={styles.container}>
      <label className={styles.teamMemberLabel}>
        <span className={styles.teamMemberLabelText}>
          {format('team-members')}
        </span>
        <MemberMultiSelectAutocompleteComponent {...autocompleteProps} />
      </label>
    </div>
  );
};
